import React, { useState, useEffect } from 'react';
import { Button, Input, message } from 'antd';
import { UserAddOutlined, SearchOutlined } from '@ant-design/icons';
import UserListTable from '../../components/userManagement/UserListTable';
import UserFormModal from '../../components/userManagement/UserFormModal';
import UserEditFormModal from '../../components/userManagement/UserEditFormModal';
import { useAuthOrg } from '../../context/AuthOrgContext';
import { User } from '../../interfaces/userManagement';
import CommonLayout from '../../common/LayoutBlock';
import { useBreadcrumbs } from '../../context/BreadcrumbContext';

const UserManagement: React.FC = () => {
    const { currentOrg } = useAuthOrg();
    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            {
                title: <div className="text-lg">Settings</div>,
            },
            {
                title: <div className="text-lg">User Management</div>,
            },
        ]);
    }, [setBreadcrumbs]);

    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState<User | null>(null);

    const [searchText, setSearchText] = useState('');

    const triggerRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setModalVisible(true);
    };

    const handleEditUser = (user: User) => {
        setEditingUser(user);
        setModalVisible(true);
    };



    const handleDeactivate = async (userId: string) => {
        // Not supported by API yet
        message.info('Deactivation not supported yet');
    };

    const handleReactivate = async (userId: string) => {
        // Not supported by API yet
        message.info('Reactivation not supported yet');
    };

    return (
        <CommonLayout>
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-semibold text-gray-800">User Management</h1>
                    <p className="text-gray-500">Manage users and their roles within {currentOrg?.orgName}</p>
                </div>
                <Button
                    type="primary"
                    className="bg-[#1F344C]"
                    icon={<UserAddOutlined />}
                    onClick={handleAddUser}
                >
                    Add User
                </Button>
            </div>

            <div className="flex gap-4">
                <Input
                    placeholder="Search users..."
                    prefix={<SearchOutlined />}
                    className="max-w-md"
                    value={searchText}
                    onChange={e => setSearchText(e.target.value)}
                    onPressEnter={triggerRefresh}
                />
            </div>

            <div className='bg-gray-50 p-4'>
                <UserListTable
                    refreshTrigger={refreshTrigger}
                    onEdit={handleEditUser}
                    onDeactivate={handleDeactivate}
                    onReactivate={handleReactivate}
                />
            </div>

            {editingUser ? (
                <UserEditFormModal
                    visible={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onSuccess={() => {
                        setModalVisible(false);
                        triggerRefresh();
                    }}
                    initialValues={editingUser}
                />
            ) : (
                <UserFormModal
                    visible={modalVisible}
                    onCancel={() => setModalVisible(false)}
                    onSuccess={() => {
                        setModalVisible(false);
                        triggerRefresh();
                    }}
                    initialValues={null}
                />
            )}
        </CommonLayout>
    );
};

export default UserManagement;
